import axios from 'axios';

const api = axios.create({ baseURL: '/api', withCredentials: true });

api.interceptors.response.use(
  r => r,
  err => {
    const url = err.config?.url || '';
    const isAuthCheck = url.includes('/auth/me') || url.includes('/auth/login') || url.includes('/auth/admin')
      || url.includes('/volume-discounts/effective'); // Phase 8: CartContext calls this app-wide, including on /login before a session exists — a 401 here is expected, not a real auth failure
    const isAdmin = window.location.pathname.includes('/admin');
    const isPSP   = window.location.pathname.includes('/psp');
    if (err.response?.status === 401 && !isAuthCheck && !isAdmin && !isPSP) {
      sessionStorage.removeItem('solidiphy_cart'); // hard navigation below bypasses CartProvider's React cleanup effect — clear directly
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// Auth
export const getConfig      = ()         => api.get('/config');
export const getMe          = ()         => api.get('/auth/me');
export const getGlobalSettings    = ()    => api.get('/settings');
export const getRemitSettings     = ()    => api.get('/settings/remit'); // admin only — Remit To bank details
export const updateGlobalSettings = (d)   => api.put('/settings', d);
export const login          = (pin)      => api.post('/auth/login', { pin });
export const logout         = ()         => api.post('/auth/logout');
export const adminLogin     = (password) => api.post('/auth/admin', { password });

// Admin user auth
export const adminUserLogin       = (email, password)  => api.post('/admin-auth/login', { email, password });
export const adminHasUsers        = ()                 => api.get('/admin-auth/has-users');
export const adminUserVerify2FA   = (code)             => api.post('/admin-auth/verify-2fa', { code });
export const adminUserLogout      = ()                 => api.post('/admin-auth/logout');
export const adminUserMe          = ()                 => api.get('/admin-auth/me');
export const adminForgotPassword  = (email)            => api.post('/admin-auth/forgot-password', { email });
export const adminResetPassword   = (token, password)  => api.post('/admin-auth/reset-password', { token, password });
// Admin user management
export const getAdminUsers        = ()      => api.get('/admin-auth/users');
export const createAdminUser      = (d)     => api.post('/admin-auth/users', d);
export const updateAdminUser      = (id, d) => api.put(`/admin-auth/users/${id}`, d);
export const toggleAdminUser      = (id)    => api.post(`/admin-auth/users/${id}/toggle-active`);
// 2FA setup
export const getTotpSetup         = ()      => api.get('/admin-auth/totp/setup');
export const verifyTotpSetup      = (code)  => api.post('/admin-auth/totp/verify-setup', { code });
export const setupSms2FA          = (phone) => api.post('/admin-auth/2fa/setup-sms', { phone });
export const verifySms2FASetup    = (code)  => api.post('/admin-auth/2fa/verify-sms-setup', { code });
export const disable2FA           = ()      => api.post('/admin-auth/2fa/disable');

// Products
export const getProducts    = (p={})     => api.get('/products', { params: p });
export const getCategories  = ()         => api.get('/products/categories');
export const getProduct     = (id)       => api.get(`/products/${id}`);

// Orders
export const getOrders      = (p={})     => api.get('/orders', { params: p });
export const getOrder       = (id)       => api.get(`/orders/${id}`);
export const createOrder    = (data)     => api.post('/orders', data);
export const linkPhoto      = (oid,iid,d)=> api.patch(`/orders/${oid}/items/${iid}/photo`, d);
export const fulfillOrder   = (id, body)       => api.patch(`/orders/${id}/fulfill`, body || {});
export const cancelOrder    = (id)       => api.patch(`/orders/${id}/cancel`);

// Shipping
export const getShippingRates = (address,items) => api.post('/shipping/rates', { address, items });
export const getTax           = (province,sub)  => api.post('/shipping/tax', { province, subtotal: sub });

// Phase 6 — Stripe tax (direct_wholesale only)
export const calculateStripeTax = (d) => api.post('/orders/calculate-tax', d);

// Uploads
export const uploadProductImage = (form) => api.post('/uploads/product-image', form, { headers: { 'Content-Type': 'multipart/form-data' } });
export const uploadLogo         = (form) => api.post('/uploads/logo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
export const uploadTaxExemptCert = (form) => api.post('/uploads/tax-exempt-cert', form, { headers: { 'Content-Type': 'multipart/form-data' } });
export const uploadPhoto        = (form) => api.post('/uploads/photo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
export const replacePhoto       = (form) => api.post('/uploads/photo/replace', form, { headers: { 'Content-Type': 'multipart/form-data' } });

// Admin — companies
export const getAdminCompanies = ()      => api.get('/admin/companies');
export const createCompany     = (d)     => api.post('/admin/companies', d);
export const updateCompany     = (id,d)  => api.put(`/admin/companies/${id}`, d);
export const deleteCompany     = (id)    => api.delete(`/admin/companies/${id}`);

// Admin — groups
export const getAdminGroups    = (p={})  => api.get('/admin/groups', { params: p });
export const createGroup       = (d)     => api.post('/admin/groups', d);
export const updateGroup       = (id,d)  => api.put(`/admin/groups/${id}`, d);
export const deleteGroup       = (id)    => api.delete(`/admin/groups/${id}`);

// Admin — products (global)
export const getAdminProducts     = (p={})    => api.get('/admin/products', { params: p });
export const createProduct        = (d)       => api.post('/admin/products', d);
export const updateProduct        = (id,d)    => api.put(`/admin/products/${id}`, d);
export const deleteProduct        = (id)      => api.delete(`/admin/products/${id}`);
// Admin — company product assignments
export const getCompanyProducts   = (p={})    => api.get('/admin/company-products', { params: p });
export const createCompanyProduct = (d)       => api.post('/admin/company-products', d);
export const updateCompanyProduct = (id,d)    => api.put(`/admin/company-products/${id}`, d);
export const deleteCompanyProduct = (id)      => api.delete(`/admin/company-products/${id}`);

// Admin — orders & stats
export const getAdminStats        = (params={})    => api.get('/admin/stats', { params });
export const getRevenueTrend      = (params={})    => api.get('/admin/revenue-trend', { params });
export const getAdminOrders       = (p={})         => api.get('/admin/orders', { params: p });
export const getAdminOrder        = (id)           => api.get(`/admin/orders/${id}`);
export const updateOrderStatus    = (id, status, note)   => api.patch(`/admin/orders/${id}/status`, { status, note });   // note: required for received_rejected (Phase 12f)
export const updateItemStatus     = (orderId, itemId, itemStatus, note) => api.patch(`/admin/orders/${orderId}/items/${itemId}/status`, { itemStatus, note });
export const cancelOrderAdmin     = (id, cancelReason) => api.post(`/admin/orders/${id}/cancel`, { cancelReason });
export const refundOrder          = (id, amount, reason) => api.post(`/admin/orders/${id}/refund`, { amount, reason });
export const dismissTaxFallback     = (id) => api.post(`/admin/orders/${id}/dismiss-tax-fallback`);
export const dismissAllTaxFallback  = ()   => api.post('/admin/tax-fallback/dismiss-all');
export const uncancelOrder        = (id)           => api.post(`/admin/orders/${id}/uncancel`);
export const cancelOrder_admin    = (id)           => api.patch(`/admin/orders/${id}/status`, { status: 'cancelled' });
export const getOrderAuditLog     = (id)           => api.get(`/admin/orders/${id}/audit`);
export const getAdminLineItems    = (p={})         => api.get('/admin/line-items', { params: p });
// Phase 9
export const updateOrderRush      = (id, isRush)   => api.put(`/admin/orders/${id}`, { isRush });
export const qcDecisionAdmin      = (orderId, itemId, result, reason) => api.patch(`/admin/orders/${orderId}/items/${itemId}/qc`, { result, reason });

// Coupons
export const getAdminCoupons      = ()          => api.get('/coupons');
export const createCoupon         = (d)         => api.post('/coupons', d);
export const updateCoupon         = (id,d)      => api.put(`/coupons/${id}`, d);
export const deleteCoupon         = (id)        => api.delete(`/coupons/${id}`);
export const validateCoupon       = (code, subtotal, country) => api.post('/coupons/validate', { code, subtotal, country });
export const validateAddress      = (address) => api.post('/orders/validate-address', address);

// Phase 8 — Volume promotions (codeless, quantity-based)
export const getEffectiveVolumeDiscounts = () => api.get('/volume-discounts/effective'); // photographer portal — current company's schedule
export const getAdminVolumePromotions = () => api.get('/admin/volume-promotions');
export const createVolumePromotion    = (d) => api.post('/admin/volume-promotions', d);
export const updateVolumePromotion    = (id, d) => api.put(`/admin/volume-promotions/${id}`, d);
export const deleteVolumePromotion    = (id) => api.delete(`/admin/volume-promotions/${id}`);
export const getAdminSettlements = (params={}) => api.get('/admin/settlements', { params });
export const exportSettlementsExcel = (settlementIds) => api.post('/admin/settlements/export-excel', { settlementIds }, { responseType: 'blob' });

// ── Partner Invoices (Phase 10) ─────────────────────────────────────────────
export const getAdminInvoices          = (params={}) => api.get('/admin/invoices', { params });
export const getInvoicePreview         = (params={}) => api.get('/admin/invoices/preview', { params });
export const getUninvoicedSummary      = ()          => api.get('/admin/invoices/uninvoiced-summary');
export const generateInvoice           = (data)       => api.post('/admin/invoices', data);
export const getInvoiceDetail          = (id)         => api.get(`/admin/invoices/${id}`);
export const downloadInvoicePdf         = (id)         => api.get(`/admin/invoices/${id}/pdf`,         { responseType: 'blob' });
export const downloadInvoiceSummaryPdf = (id)         => api.get(`/admin/invoices/${id}/pdf/summary`, { responseType: 'blob' });
export const downloadInvoiceBackupPdf  = (id)         => api.get(`/admin/invoices/${id}/pdf/backup`,  { responseType: 'blob' });
export const downloadInvoiceExcel      = (id)         => api.get(`/admin/invoices/${id}/excel`,       { responseType: 'blob' });
export const voidInvoice               = (id, reason) => api.post(`/admin/invoices/${id}/void`, { reason });
export const markInvoicePaid           = (id, paid=true) => api.patch(`/admin/invoices/${id}/paid`, { paid });

// PSP Auth
export const pspLogin             = (d)           => api.post('/psp-auth/login', d);
export const pspLogout            = ()            => api.post('/psp-auth/logout');
export const pspMe                = ()            => api.get('/psp-auth/me');
export const pspForgotPassword    = (email)       => api.post('/psp-auth/forgot-password', { email });
export const pspResetPassword     = (token, password) => api.post('/psp-auth/reset-password', { token, password });
export const pspChangePassword    = (password)    => api.post('/psp-auth/change-password', { password });
export const getPSPUsers          = ()            => api.get('/psp-auth/users');
export const createPSPUser        = (d)           => api.post('/psp-auth/users', d);
export const updatePSPUser        = (id, d)       => api.put(`/psp-auth/users/${id}`, d);
export const togglePSPUser        = (id)          => api.patch(`/psp-auth/users/${id}/toggle`);

// PSP Portal
export const getPSPOrders         = (p={})        => api.get('/psp/orders', { params: p });
export const getPSPOrder          = (id)          => api.get(`/psp/orders/${id}`);
export const getPSPLineItems      = (p={})        => api.get('/psp/line-items', { params: p });
export const getPSPSKUs           = ()            => api.get('/psp/skus');
export const updatePSPOrderStatus = (id, status, extra = {}) => api.patch(`/psp/orders/${id}/status`, { status, ...extra });
export const updatePSPItemStatus  = (orderId, itemId, itemStatus) => api.patch(`/psp/orders/${orderId}/items/${itemId}/status`, { itemStatus });
export const bulkUpdatePSPItemStatus = (itemIds, itemStatus) => api.patch('/psp/items/bulk-status', { itemIds, itemStatus });
export const createPackingSlip    = (itemIds) => api.post('/psp/packing-slips', { itemIds });
export const getPSPPackingSlips   = ()        => api.get('/psp/packing-slips');
export const discardPSPPackingSlip = (slipId) => api.patch(`/psp/packing-slips/${slipId}/discard`);
export const getAdminPackingSlips  = ()        => api.get('/admin/packing-slips');
export const discardAdminPackingSlip = (slipId) => api.patch(`/admin/packing-slips/${slipId}/discard`);
export const getPSPItemFiles      = (orderId, itemId) => api.get(`/psp/orders/${orderId}/items/${itemId}/files`);
export const uploadModeledFile    = (orderId, itemId, formData) => api.post(`/psp/orders/${orderId}/items/${itemId}/upload-modeled`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
// Phase 9
export const getPSPItemPartnerData = (orderId, itemId) => api.get(`/psp/orders/${orderId}/items/${itemId}/partner-data`); // Phase 12
export const getPSPItemModels      = (orderId, itemId) => api.get(`/psp/orders/${orderId}/items/${itemId}/models`);       // Phase 12b
export const qcDecisionPSP        = (orderId, itemId, result, reason) => api.patch(`/psp/orders/${orderId}/items/${itemId}/qc`, { result, reason });

// Training videos (Phase 9) — photographer Resources page
export const getTrainingVideos    = ()            => api.get('/training-videos');
export const trainingVideoUrl     = (id)          => `/api/training-videos/${id}`;

// Admin — Meshy Tasks (Phase 5)
export const getAdminMeshyTasks   = (status)          => api.get('/admin/meshy-tasks', { params: status ? { status } : {} });
export const retryAdminMeshyTask  = (id, payload)     => api.post(`/admin/meshy-tasks/${id}/retry`, payload || {});

export default api;

// Phase 12 — PSP companies, Partner API keys, webhooks, assignment, model review (admin)
export const getPSPCompanies          = ()             => api.get('/admin/psp/companies');
export const createPSPCompany         = (d)            => api.post('/admin/psp/companies', d);
export const updatePSPCompany         = (id, d)        => api.put(`/admin/psp/companies/${id}`, d);
export const generatePSPWebhookSecret = (id)           => api.post(`/admin/psp/companies/${id}/webhook-secret`);
export const revealPSPWebhookSecret   = (id)           => api.get(`/admin/psp/companies/${id}/webhook-secret`);
export const testPSPWebhook           = (id)           => api.post(`/admin/psp/companies/${id}/test-webhook`);
export const createPSPApiKey          = (id, label)    => api.post(`/admin/psp/companies/${id}/keys`, { label });
export const revokePSPApiKey          = (keyId)        => api.post(`/admin/psp/keys/${keyId}/revoke`);
export const assignItemsToPSP         = (itemIds, pspCompanyId) => api.post('/admin/psp/assign', { itemIds, pspCompanyId });
export const reviewPSPModel           = (itemId, decision, reason) => api.patch(`/admin/psp/items/${itemId}/model-review`, { decision, reason });
export const getAdminItemPartnerData  = (itemId)       => api.get(`/admin/psp/items/${itemId}/partner-data`);
export const getAdminItemModels       = (itemId)       => api.get(`/admin/psp/items/${itemId}/models`); // Phase 12b
export const shareMeshyWithPSP        = (itemId, share) => api.patch(`/admin/psp/items/${itemId}/share-meshy`, { share }); // Phase 12d
export const getAdminOrderSlips       = (orderId)      => api.get(`/admin/psp/orders/${orderId}/packing-slips`);         // Phase 12f
export const getAdminItemShipments    = (itemId)       => api.get(`/admin/psp/items/${itemId}/shipments`);               // Phase 12f
export const getAdminSlipPrintData    = (slipId)       => api.get(`/admin/psp/packing-slips/${slipId}/print-data`);       // Phase 12e
export const getPSPWebhookDeliveries  = (p={})         => api.get('/admin/psp/webhook-deliveries', { params: p });
export const retryPSPWebhookDelivery  = (id)           => api.post(`/admin/psp/webhook-deliveries/${id}/retry`);

// Phase 12 — admin view of the PSP portal. Marks /api/psp calls so the server
// uses the admin session; downloads (window.open / iframes can't send
// headers) carry ?adminView=1 instead.
let pspAdminView = false;
export const setPSPAdminView = (on) => {
  pspAdminView = !!on;
  if (pspAdminView) api.defaults.headers.common['X-PSP-Admin-View'] = '1';
  else delete api.defaults.headers.common['X-PSP-Admin-View'];
};
export const pspDownloadUrl = (path, filename) =>
  `/api/psp/download?path=${encodeURIComponent(path)}&filename=${encodeURIComponent(filename)}${pspAdminView ? '&adminView=1' : ''}`;
